import { Autocomplete, Box, Stack, Typography } from '@mui/material';
import { Chip, Show, TextField } from '@side/pantry';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { TrackOfferEvent } from 'src/modules/analytics/useTrackAnalytics/domains/offers/events';
import { useTrackOfferAnalytics } from 'src/modules/analytics/useTrackAnalytics/domains/offers/useTrackOfferAnalytics';
import type { SupportedStateCode } from '@/common/constants/states';
import { STATE_CODE_TO_NAME } from '@/common/constants/states';
import { getFullName } from '@/common/utils/person';
import { ClientGroupName } from '@/components/ClientGroupName/ClientGroupName';
import { formatClientGroupNameStringWithAgents } from '@/components/ClientGroupName/helpers';
import { DropdownLoadingIcon } from '@/components/DropdownLoadingIcon';
import type { CoreRequest } from '$api-clients/hooks';
import { useCreateOfferContext } from '../../context/CreateOfferContext';
import {
  BuyerAgreementStatus,
  getBuyerAgreementStatus,
} from '../../hooks/useBuyerAgreementStatus';
import { useGetClientGroupsForState } from '../../hooks/useGetClientGroupsForState';
import type { CreateOfferFormData } from '../../types';

type ClientGroupContact =
  CoreRequest<'/client-groups'>['Data']['results'][number]['contacts'][number];

export const BuyersAutocomplete = () => {
  const {
    clientGroups,
    loading: clientGroupsLoading,
    setSearchTerm,
    debouncedSearchTerm,
  } = useGetClientGroupsForState();
  const {
    register,
    setValue,
    formState: { touchedFields },
  } = useFormContext();
  const [selectedValue, state] = useWatch<
    CreateOfferFormData,
    ['buyer', 'state']
  >({ name: ['buyer', 'state'] });
  const isTouched = Boolean(touchedFields.buyer);
  const { defaultBuyer, defaultBuyerLoading } = useCreateOfferContext();
  const trackOfferEvent = useTrackOfferAnalytics();

  const [hasBeenBlurred, setHasBeenBlurred] = useState(false);

  // Auto-select buyer when data is loaded
  useEffect(() => {
    if (defaultBuyer && !selectedValue && !isTouched) {
      setValue('buyer', defaultBuyer, { shouldValidate: true });
    }
  }, [defaultBuyer, selectedValue, isTouched, setValue]);

  // Make sure defaultBuyer is included in options list
  const options = useMemo(() => {
    if (!defaultBuyer) return clientGroups;

    const isInOptions = clientGroups.some((cg) => cg.id === defaultBuyer.id);
    if (isInOptions) return clientGroups;

    return [defaultBuyer, ...clientGroups].sort((a, b) => {
      // Sort by createdAt desc (newest first)
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [clientGroups, defaultBuyer]);

  const loading = clientGroupsLoading || defaultBuyerLoading;

  return (
    <Autocomplete
      {...register('buyer', { required: true })}
      options={options}
      clearOnBlur
      autoComplete
      loading={loading}
      value={selectedValue ?? null}
      onChange={(_, value) => {
        if (!value) {
          setValue('buyer', null, { shouldTouch: true, shouldValidate: true });
          trackOfferEvent(TrackOfferEvent.ClearExistingBuyer, {
            clientGroupId: selectedValue?.id ?? null,
          });
        } else if (typeof value === 'object') {
          setValue('buyer', value, { shouldTouch: true, shouldValidate: true });
          trackOfferEvent(TrackOfferEvent.SelectExistingBuyer, {
            clientGroupId: value.id,
            hasAgreement:
              getBuyerAgreementStatus(value) === BuyerAgreementStatus.SIGNED,
          });
        }
        setSearchTerm('');
      }}
      noOptionsText={
        <Show when={options.length === 0 && !debouncedSearchTerm}>
          <Typography sx={{ color: 'text.primary' }}>
            There are no saved Buyers in{' '}
            {STATE_CODE_TO_NAME[state as SupportedStateCode] || state}. Create a
            new Buyer to move forward.
          </Typography>
        </Show>
      }
      renderInput={(params) => (
        <TextField
          {...params}
          onChange={(e) => {
            setSearchTerm(e.target.value);
          }}
          placeholder="Select from existing Buyers"
          label="Existing Buyer"
          fullWidth
          required
          error={hasBeenBlurred && !selectedValue}
          helperText={hasBeenBlurred && !selectedValue ? 'Required' : undefined}
          highlight={!selectedValue ? 'secondary' : undefined}
          onBlur={() => {
            setHasBeenBlurred(true);
          }}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: loading ? (
                <DropdownLoadingIcon />
              ) : (
                params.slotProps.input.endAdornment
              ),
            },
          }}
        />
      )}
      renderOption={({ key: _key, ...optionProps }, option) => {
        const agents = option.contacts.filter(
          (c: ClientGroupContact) => c.type === 'agent',
        );
        const agentNames = agents
          .map((agent: ClientGroupContact) =>
            getFullName(agent.firstName, agent.lastName),
          )
          .filter((name: string | null) => name)
          .join(', ');

        return (
          <Box {...optionProps} key={option.id} component="li">
            <Stack
              sx={{
                flexDirection: { xs: 'column', lg: 'row' },
                justifyContent: 'space-between',
                gap: 1,
                py: { xs: 0.75, lg: 0.25 },
                alignItems: 'flex-start',
                width: '100%',
              }}
            >
              <Stack
                sx={{
                  alignItems: 'flex-start',
                  width: '100%',
                }}
              >
                <ClientGroupName contacts={option.contacts} noWrap />

                <Typography
                  noWrap
                  variant="body3"
                  sx={{ color: 'text.secondary' }}
                >
                  {agents.length > 1
                    ? `Transacting Agents: ${agentNames}`
                    : `Transacting Agent: ${agentNames}`}
                </Typography>
              </Stack>
              <Show
                when={
                  getBuyerAgreementStatus(option) ===
                  BuyerAgreementStatus.SIGNED
                }
              >
                <Chip
                  label="Signed Buyer Agreement"
                  variant="outlined"
                  color="success"
                  size="small"
                  sx={{
                    width: 'fit-content',
                    alignSelf: { xs: 'flex-start', lg: 'center' },
                  }}
                />
              </Show>
            </Stack>
          </Box>
        );
      }}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;

        // The selected label must be a string, not a ReactNode
        return formatClientGroupNameStringWithAgents(option.contacts);
      }}
      isOptionEqualToValue={(option, value) => {
        return option.id === value.id;
      }}
      slotProps={{
        popper: {
          sx: {
            '.MuiAutocomplete-noOptions:empty': {
              display: 'none',
            },
          },
        },
      }}
    />
  );
};
